import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function GoogleAutoAuth() {
  const { isAuth, loading, isGuest, loginWithGoogle } = useAuth();
  const location = useLocation();
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '194102935146-hjbhf90jhc6o79i2qognfb8mgi1q08q5.apps.googleusercontent.com';

  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';
  const isAndroidApp = !!window.ShopAxoBridge?.triggerGoogleSignIn;

  useEffect(() => {
    // If on auth pages, session loading, or user is already logged in, do NOT trigger
    if (isAuthPage || loading || isAuth || !isGuest) return;

    window.handleGoogleOneTapCallback = async (response) => {
      if (!response?.credential) return;
      const toastId = toast.loading('Signing in with Google...');
      try {
        const user = await loginWithGoogle(response.credential);
        toast.success(`Welcome ${user?.name || 'User'}! Signed in with Google.`, { id: toastId });
      } catch (err) {
        console.error('Google One-Tap auth failed:', err);
        toast.dismiss(toastId);
      }
    };

    // Android App specific rule: Show ONCE per app launch session
    if (isAndroidApp) {
      const hasPromptedAndroid = sessionStorage.getItem('android_onetap_dismissed');
      if (!hasPromptedAndroid) {
        sessionStorage.setItem('android_onetap_dismissed', 'true');
        console.log('[GoogleAutoAuth] Triggering Android Native Google Sign-In (Once per app session)');
        window.ShopAxoBridge.triggerGoogleSignIn();
      }
      return;
    }

    // Web Browser: Standard Google One-Tap behavior
    const triggerPrompt = () => {
      if (window.google?.accounts?.id) {
        try {
          window.google.accounts.id.cancel();
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: window.handleGoogleOneTapCallback,
            auto_select: false,
            cancel_on_tap_outside: true
          });
          window.google.accounts.id.prompt();
        } catch (e) {
          console.error('[GoogleOneTap] Failed to initialize web prompt:', e);
        }
      }
    };

    const timer = setTimeout(triggerPrompt, 1000);
    return () => clearTimeout(timer);
  }, [isAuthPage, loading, isAuth, isGuest, isAndroidApp, loginWithGoogle, clientId]);

  if (isAuthPage || loading || isAuth || !isGuest || isAndroidApp) return null;

  return (
    <div 
      id="g_id_onload"
      data-client_id={clientId}
      data-callback="handleGoogleOneTapCallback"
      data-auto_prompt="true"
      data-cancel_on_tap_outside="true"
    />
  );
}
